import { useCallback, useEffect, useMemo, useState } from "react";

import clsx from "clsx";
import moment from "moment";

import { DatePicker } from "@mantine/dates";
import { type DateInterval } from "@north-beam/nb-common";

import { DateInput } from "../date-input/date-input";

import "./calendar.scss";

interface Props {
  variant?: "benchmarks";
  disabled?: boolean;
  minDate?: string;
  maxDate?: string;
  value: DateInterval | null;
  onChange: (val: DateInterval) => void;
}

export const Calendar = ({
  variant,
  disabled,
  minDate,
  maxDate,
  value,
  onChange,
}: Props) => {
  const localTimezone = moment.tz.guess();

  const [internalValue, setInternalValue] = useState<
    [Date | null, Date | null]
  >([null, null]);

  const [startDate, endDate] = internalValue;

  useEffect(() => {
    if (value) {
      setInternalValue([
        moment.tz(value.startDate, localTimezone).toDate(),
        moment.tz(value.endDate, localTimezone).toDate(),
      ]);
    } else {
      setInternalValue([null, null]);
    }
  }, [value]);

  const handleDateChange = useCallback(
    ({
      startDate: newStartDate,
      endDate: newEndDate,
    }: {
      startDate: Date | null;
      endDate: Date | null;
    }) => {
      onChange({
        startDate: moment
          .tz(newStartDate ?? startDate, localTimezone)
          .format("YYYY-MM-DD"),
        endDate: moment
          .tz(newEndDate ?? endDate, localTimezone)
          .format("YYYY-MM-DD"),
      });
    },
    [onChange, startDate, endDate],
  );

  const minDateValue = useMemo(
    () => (minDate ? moment.tz(minDate, localTimezone).toDate() : undefined),
    [minDate],
  );
  const maxDateValue = useMemo(
    () => (maxDate ? moment.tz(maxDate, localTimezone).toDate() : undefined),
    [maxDate],
  );

  return (
    <div>
      <DatePicker
        className={clsx("calendar-v2", variant, { disabled })}
        classNames={{
          calendarHeader: "calendar-header",
          calendarHeaderLevel: "calendar-header-level",
          calendarHeaderControl: "calendar-header-control",
          levelsGroup: "levels-group",
          monthCell: "table-cell",
          weekday: "weekday",
          day: "day",
        }}
        previousIcon={<i className="fa-solid fa-chevron-left" />}
        nextIcon={<i className="fa-solid fa-chevron-right" />}
        renderDay={(date) => <span>{date.getDate()}</span>}
        type="range"
        allowSingleDateInRange
        numberOfColumns={2}
        columnsToScroll={1}
        firstDayOfWeek={0}
        level="month"
        minDate={minDateValue}
        maxDate={maxDateValue}
        defaultDate={moment.tz(value?.endDate, localTimezone).toDate()}
        value={internalValue}
        onChange={(val) => {
          setInternalValue(val);

          if (val[0] && val[1]) {
            onChange({
              startDate: moment.tz(val[0], localTimezone).format("YYYY-MM-DD"),
              endDate: moment.tz(val[1], localTimezone).format("YYYY-MM-DD"),
            });
          }
        }}
      />
      <div className="flex items-center gap-2.5 text-sm mb-6 mt-4">
        <DateInput
          className="flex-1"
          textInputClassName="bg-white"
          label="Start Date"
          value={startDate ? moment.tz(startDate, localTimezone) : null}
          minDate={
            minDateValue ? moment.tz(minDateValue, localTimezone) : undefined
          }
          maxDate={
            maxDateValue ? moment.tz(maxDateValue, localTimezone) : undefined
          }
          placeholder="Begins..."
          onChange={(date) => {
            if (!endDate || moment(endDate).isSameOrAfter(date)) {
              setInternalValue([date.toDate(), endDate]);
              handleDateChange({ startDate: date.toDate(), endDate });
            }
          }}
        />
        <div className="w-5" />
        <DateInput
          className="flex-1"
          textInputClassName="bg-white"
          label="End Date"
          value={endDate ? moment.tz(endDate, localTimezone) : null}
          minDate={
            minDateValue ? moment.tz(minDateValue, localTimezone) : undefined
          }
          maxDate={
            maxDateValue ? moment.tz(maxDateValue, localTimezone) : undefined
          }
          placeholder="Ends..."
          onChange={(date) => {
            if (!startDate || moment(startDate).isSameOrBefore(date)) {
              setInternalValue([startDate, date.toDate()]);
              handleDateChange({ startDate, endDate: date.toDate() });
            }
          }}
        />
      </div>
    </div>
  );
};
